import { format } from "date-fns"
import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { motion, AnimatePresence } from "framer-motion"
import { cn } from "@/lib/utils"
import { Utensils, Clock, Calendar, Info, FileText } from "lucide-react"
import { formatDbTimestampToLocal } from "@/lib/utils/date"
import { Badge } from "@/components/ui/badge"
import { useMediaQuery } from "@/hooks/use-media-query"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

interface FeedingRecordsTableProps {
  childId: string
  limit?: number
  timezone?: string
}

export function FeedingRecordsTable({ childId, limit, timezone }: FeedingRecordsTableProps) {
  const isSmall = useMediaQuery("(max-width: 640px)");
  const isMobile = useMediaQuery("(max-width: 479px)");
  
  const { data: feedingRecords, isLoading } = useQuery({
    queryKey: ['feedingRecords', childId, limit],
    queryFn: async () => {
      let query = supabase
        .from('feeding_sessions')
        .select(`
          *,
          bottle_sessions (
            amount_ml,
            amount_oz
          ),
          breastfeeding_sessions (
            left_duration,
            right_duration,
            latch_quality
          )
        `)
        .eq('child_id', childId)
        .order('start_time', { ascending: false })

      if (limit) {
        query = query.limit(limit)
      }

      const { data, error } = await query

      if (error) throw error
      return data
    },
    enabled: !!childId
  })

  if (isLoading) {
    return (
      <div className="p-4 text-center text-pink-300/70 animate-pulse">
        Loading feeding records...
      </div>
    )
  }

  if (!feedingRecords?.length) {
    return (
      <div className="p-4 text-center text-gray-400">
        No feeding records found
      </div>
    )
  }

  // Get the user's local timezone if none is specified
  const userTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timezoneAbbr = userTimezone ? 
    new Intl.DateTimeFormat('en-US', { timeZoneName: 'short', timeZone: userTimezone })
      .formatToParts(new Date())
      .find(part => part.type === 'timeZoneName')?.value || userTimezone
    : 'Local';

  return (
    <div className="rounded-xl overflow-hidden border border-pink-500/20 backdrop-blur-xl bg-pink-500/5">
      <div className="p-3 sm:p-4 bg-gradient-to-r from-pink-500/10 to-rose-500/10 border-b border-pink-500/20">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Feeding Records</h3>
          <div className="text-xs text-gray-400">
            All times shown in {timezoneAbbr}
          </div>
        </div>
      </div>

      <div className="p-2 sm:p-4 overflow-x-auto">
        <div className="rounded-lg overflow-hidden min-w-[600px] sm:min-w-0">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-pink-500/20 hover:bg-transparent bg-slate-800/20">
                <TableHead className="text-xs sm:text-sm text-pink-300 py-2 sm:py-3.5 font-semibold">Start Date</TableHead>
                <TableHead className="text-xs sm:text-sm text-pink-300 py-2 sm:py-3.5 font-semibold">End Date</TableHead>
                <TableHead className="text-xs sm:text-sm text-pink-300 py-2 sm:py-3.5 font-semibold">Time Window</TableHead>
                <TableHead className="text-xs sm:text-sm text-pink-300 py-2 sm:py-3.5 font-semibold">Type</TableHead>
                <TableHead className="text-xs sm:text-sm text-pink-300 py-2 sm:py-3.5 font-semibold">Amount</TableHead>
                {!isSmall && (
                  <TableHead className="text-xs sm:text-sm text-pink-300 py-2 sm:py-3.5 font-semibold">
                    <div className="flex items-center space-x-1">
                      <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span>Notes</span>
                    </div>
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              <AnimatePresence>
                {feedingRecords.map((record, index) => {
                  const duration = record.end_time
                    ? new Date(record.end_time).getTime() - new Date(record.start_time).getTime()
                    : 0
                  const minutes = Math.floor(duration / (1000 * 60))
                  
                  let details = ''
                  let durationOrAmount = ''

                  if (record.type === 'breastfeeding' && record.breastfeeding_sessions?.[0]) {
                    const bs = record.breastfeeding_sessions[0]
                    durationOrAmount = `${minutes} min`
                    details = `Left: ${bs.left_duration}min, Right: ${bs.right_duration}min`
                    if (bs.latch_quality) {
                      details += `, Latch: ${bs.latch_quality}`
                    }
                  } else if (record.type === 'bottle' && record.bottle_sessions?.[0]) {
                    const bottle = record.bottle_sessions[0]
                    durationOrAmount = `${bottle.amount_ml}ml (${bottle.amount_oz}oz)`
                    if (minutes > 0) {
                      details = `Duration: ${minutes}min`
                    }
                  }

                  // Check if notes exist and are not empty
                  const hasNotes = record.notes && record.notes.trim().length > 0;

                  return (
                    <motion.tr 
                      key={record.id} 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className={cn(
                        "border-b border-pink-500/10",
                        "transition-colors duration-200",
                        "hover:bg-pink-500/5"
                      )}
                    >
                      <TableCell className="text-xs sm:text-sm text-gray-200 py-2 sm:py-3">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-pink-400" />
                          <span className="text-xs sm:text-sm">
                            {formatDbTimestampToLocal(record.start_time, "MMM DD, YYYY")}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm text-gray-200 py-2 sm:py-3">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-pink-400" />
                          <span className="text-xs sm:text-sm">
                            {record.end_time ? formatDbTimestampToLocal(record.end_time, "MMM DD, YYYY") : "In progress"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm text-gray-200 py-2 sm:py-3">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400" />
                          <span className="text-xs sm:text-sm">
                            {formatDbTimestampToLocal(record.start_time, "h:mm a")}
                            {record.end_time ? ` - ${formatDbTimestampToLocal(record.end_time, "h:mm a")}` : " - In progress"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm capitalize py-2 sm:py-3">
                        <div className="flex items-center gap-1.5">
                          <Utensils className="w-3 h-3 sm:w-4 sm:h-4 text-pink-400" />
                          <Badge variant="outline" className={cn(
                            "capitalize text-sm font-medium",
                            record.type === 'breastfeeding' && "bg-pink-500/30 border-pink-500/40 text-pink-100",
                            record.type === 'bottle' && "bg-blue-500/30 border-blue-500/40 text-blue-100",
                            record.type === 'formula' && "bg-yellow-500/30 border-yellow-500/40 text-yellow-100"
                          )}>
                            {record.type}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm text-gray-200 py-2 sm:py-3">
                        <span className="px-1.5 sm:px-2 py-0.5 rounded-full bg-pink-500/20 text-pink-200 inline-block">
                          {durationOrAmount}
                        </span>
                      </TableCell>
                      {!isSmall && (
                        <TableCell className="text-xs sm:text-sm text-gray-200 py-2 sm:py-3">
                          {hasNotes ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center cursor-help">
                                    <FileText className="w-3.5 h-3.5 text-pink-400 mr-1" />
                                    <span className="text-xs text-gray-300 truncate max-w-[100px]">
                                      {record.notes.substring(0, 15)}{record.notes.length > 15 ? '...' : ''}
                                    </span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[300px] text-xs">
                                  <p>{record.notes}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-xs text-gray-500">No notes</span>
                          )}
                        </TableCell>
                      )}
                    </motion.tr>
                  )
                })}
              </AnimatePresence>
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  )
}
